<style lang="scss">
$teal:      #5bb8b1;
$teal-dark: #2c7d76;
$teal-bg:   #e4f3f0;
$dark:      #1a1a2e;
$gray:      #6c7280;
$font:      'Segoe UI', system-ui, sans-serif;

.rv-wrap {
  background: $teal-bg !important;
  min-height: 100vh;
  font-family: $font;
  padding: 32px 16px 60px;

  @media (min-width: 768px) { padding: 48px 24px 80px; }
}

/* ── Card ── */
.rv-card {
  background: #fff;
  border-radius: 16px;
  border: 1.5px solid rgba(91, 184, 177, 0.22);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);
  max-width: 520px;
  margin: 0 auto;
  overflow: hidden;
}

/* ── Card top bar ── */
.rv-card__header {
  background: $teal-dark;
  padding: 20px 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.rv-card__service {
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  flex: 1;

  small {
    display: block;
    font-size: 11px;
    font-weight: 400;
    opacity: 0.75;
    margin-bottom: 4px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
}

.rv-card__qr {
  flex-shrink: 0;
  background: #fff;
  border-radius: 8px;
  padding: 4px;
  line-height: 0;
}

/* ── Card body ── */
.rv-card__body { padding: 20px 24px; }

/* ── Section heading ── */
.rv-section-title {
  font-size: 11px;
  font-weight: 700;
  color: $teal;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0 0 10px;
}

/* ── Appointment date/time block ── */
.rv-appt-block {
  background: rgba(91, 184, 177, 0.07);
  border: 1.5px solid rgba(91, 184, 177, 0.2);
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.rv-appt-block__date {
  font-size: 15px;
  font-weight: 700;
  color: $teal-dark;
  margin: 0 0 3px;
}

.rv-appt-block__time {
  font-size: 13px;
  color: $teal;
  font-weight: 600;
  margin: 0;
}

.rv-appt-block__amount {
  text-align: right;
  flex-shrink: 0;

  small {
    display: block;
    font-size: 10px;
    color: $gray;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  span {
    font-size: 20px;
    font-weight: 700;
    color: $teal-dark;
  }
}

/* ── Detail list ── */
.rv-list {
  list-style: none;
  padding: 0;
  margin: 0 0 18px;

  li {
    display: flex;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px solid rgba(91, 184, 177, 0.1);
    font-size: 13px;

    &:last-child { border-bottom: none; }
  }
}

.rv-list__label {
  color: $gray;
  white-space: nowrap;
  flex-shrink: 0;
}

.rv-list__value {
  font-weight: 600;
  color: $dark;
  word-break: break-word;
}

.rv-map-link {
  color: $teal;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;

  &:hover { color: $teal-dark; text-decoration: underline; }
}

/* ── Divider ── */
.rv-divider {
  border: none;
  border-top: 1px solid rgba(91, 184, 177, 0.15);
  margin: 16px 0;
}

/* ── Footer row ── */
.rv-card__footer {
  padding: 16px 24px 20px;
  display: flex;
  justify-content: flex-end;
}

.rv-close-btn {
  height: 38px;
  padding: 0 24px;
  border-radius: 8px;
  border: none;
  background: $teal-dark;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: $font;
  transition: background 0.15s;

  &:hover { background: darken(#2c7d76, 8%); }
}

/* ── Loading / error states ── */
.rv-state-card {
  background: #fff;
  border-radius: 14px;
  border: 1.5px solid rgba(91, 184, 177, 0.2);
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  max-width: 400px;
  margin: 0 auto;
  padding: 32px 24px;
  text-align: center;

  h6, h5 { color: $teal-dark; font-weight: 700; margin: 12px 0 0; }
}
</style>

<template>
  <section class="rv-wrap content d-flex flex-column flex-column-fluid" id="f-succes" ref="htmlTempForPdf">
    <div class="d-flex flex-column-fluid">
      <div class="container-fluid">

        <!-- Loading -->
        <div v-if="loadingData" class="rv-state-card">
          <loadingSpinner></loadingSpinner>
          <h6>Please wait, fetching your appointment details…</h6>
        </div>

        <!-- Main card -->
        <div class="rv-card" v-if="hasData && !loadingData">

          <!-- Header: service + QR -->
          <div class="rv-card__header">
            <div class="rv-card__service">
              <small>Service</small>
              {{ appointmentData.service_name }}
            </div>
            <div class="rv-card__qr">
              <qr-code :text="qrValue" :size="72" bg-color="#ffffff" error-level="L"></qr-code>
            </div>
          </div>

          <div class="rv-card__body">

            <!-- Appointment date/time + amount -->
            <div v-if="appointmentData.appointment_date != null">
              <p class="rv-section-title">Appointment Details</p>
              <div class="rv-appt-block">
                <div>
                  <p class="rv-appt-block__date">{{ appointmentData.appointment_date | sDateFormate }}</p>
                  <p class="rv-appt-block__time">
                    {{ appointmentData.start_time | timeFormat(appointmentData.appointment_date) }}
                    {{ appointmentData.client_timezone }}
                  </p>
                </div>
                <div class="rv-appt-block__amount" v-if="getAbbreviationCode.data.show_payment_option">
                  <small>Total</small>
                  <span>${{ appointmentData.total_amount | toFixedTwo }}</span>
                </div>
              </div>
            </div>

            <!-- Amount without date -->
            <div v-if="appointmentData.appointment_date == null && getAbbreviationCode.data.show_payment_option" class="rv-appt-block" style="margin-bottom:20px">
              <div></div>
              <div class="rv-appt-block__amount">
                <small>Total</small>
                <span>${{ appointmentData.total_amount | toFixedTwo }}</span>
              </div>
            </div>

            <hr class="rv-divider" />

            <div class="row">
              <!-- Personal details -->
              <div class="col-md-6 col-12 mb-3">
                <p class="rv-section-title">Personal Details</p>
                <ul class="rv-list">
                  <li>
                    <span class="rv-list__label">First Name:</span>
                    <span class="rv-list__value">{{ appointmentData.fname }}</span>
                  </li>
                  <li v-if="appointmentData.mname != ''">
                    <span class="rv-list__label">Middle Name:</span>
                    <span class="rv-list__value">{{ appointmentData.mname }}</span>
                  </li>
                  <li>
                    <span class="rv-list__label">Last Name:</span>
                    <span class="rv-list__value">{{ appointmentData.lname }}</span>
                  </li>
                  <li>
                    <span class="rv-list__label">Email:</span>
                    <span class="rv-list__value">{{ appointmentData.email }}</span>
                  </li>
                  <li>
                    <span class="rv-list__label">Phone:</span>
                    <span class="rv-list__value">{{ appointmentData.phone_no }}</span>
                  </li>
                </ul>
              </div>

              <!-- Location details -->
              <div class="col-md-6 col-12 mb-3">
                <p class="rv-section-title">Location Details</p>
                <ul class="rv-list">
                  <li>
                    <span class="rv-list__label">Name:</span>
                    <span class="rv-list__value">
                      <template v-if="appointmentData.location_job">{{ appointmentData.job_client_name }}</template>
                      <template v-else>{{ appointmentData.name }}</template>
                    </span>
                  </li>
                  <li>
                    <span class="rv-list__label">Address:</span>
                    <span class="rv-list__value">
                      <template v-if="appointmentData.location_job">{{ appointmentData.job_client_address }}</template>
                      <template v-else>{{ appointmentData.address }}</template>
                    </span>
                  </li>
                  <li>
                    <a
                      class="rv-map-link"
                      :href="'http://www.google.com/maps/place/' + appointmentData.lat + ',' + appointmentData.long"
                      target="_blank"
                    ><i class="fa fa-map-marker-alt mr-1"></i>Find on map</a>
                  </li>
                </ul>
              </div>
            </div>

            <p style="font-size:12px;color:#6c7280;margin:0">
              We have also e-mailed your appointment details to your email address.
            </p>

          </div>

          <div class="rv-card__footer">
            <button class="rv-close-btn" @click="goHome()">Close</button>
          </div>

        </div>

        <!-- No data -->
        <div class="rv-state-card" v-if="!hasData && !loadingData">
          <i class="fa fa-calendar-times" style="font-size:36px;color:#b0d4d1"></i>
          <h5>Appointment not found</h5>
          <p style="font-size:13px;color:#6c7280;margin-top:8px">Sorry, we couldn't find your appointment details.</p>
          <button class="rv-close-btn" style="margin-top:16px" @click="goHome()">Go Back</button>
        </div>

        <canvas id="barcode" style="width:inherit;height:75px;padding:0 10px 0 0;display:none"></canvas>

      </div>
    </div>
  </section>
</template>

<script>
import VueQRCodeComponent from "vue-qrcode-component";
import loadingSpinner from "@/components/frontend/spinner.vue";
import moment from "moment";
import { SET_CHECK_F_ABBREVIATION_CODE } from "@/core/services/store/mutations.type";
import { FETCH_SELF_APPOINMENT } from "@/core/services/store/actions.type";
import * as PDF417 from "pdf417-generator";
import { mapGetters } from "vuex";

export default {
  components: {
    "qr-code": VueQRCodeComponent,
    loadingSpinner
  },
  data() {
    return {
      token: "",
      ap_id: "",
      qrValue: "",
      appointmentData: {},
      loadingData: false,
      hasData: false,
      slug: "",
      route_name: ""
    };
  },
  computed: {
    ...mapGetters(["getAbbreviationCode"])
  },
  methods: {
    goHome() {
      this.$store.commit(SET_CHECK_F_ABBREVIATION_CODE, {
        data: { show_payment_option: true },
        success: "none"
      });
      if (this.slug != "" && this.route_name != "") {
        this.$router.push({ name: this.route_name, params: { slug: this.slug } });
      } else {
        this.$router.push({ name: "frontend.home", params: { slug: this.$route.params.client_slug } });
      }
    },
    generateBarCode(value) {
      var canvas = document.getElementById("barcode");
      PDF417.draw(value, canvas);
    }
  },
  created() {
    this.loadingData = true;
    this.token = this.$route.query.token;
    this.ap_id = this.$route.query.app_id;
    this.slug = this.$route.query.slug ? this.$route.query.slug : "";
    this.route_name = this.$route.query.route_name ? this.$route.query.route_name : "";
    this.qrValue =
      window.location.origin + "/" + this.$route.params.client_slug +
      "/appointment/view?token=" + this.token + "&&app_id=" + this.ap_id;

    this.$store
      .dispatch(FETCH_SELF_APPOINMENT, { token: this.token, appointment_id: this.ap_id })
      .then(res => {
        this.appointmentData = res.data;
        this.loadingData = false;
        this.hasData = res.hasData;
        var DBB = this.$moment(res.data.dob).format("MMDDYYYY");
        var DBC = res.data.gender == "Female" ? 2 : 1;
        var DAU = ((res.data.height * 12) / 100).toFixed(2);
        var value =
          "@\n\nANSI 636020090102DL00410279ZC03200010DLDAQ171774992\nDCA\nDCB\nDCD\nDBA\nDCS" +
          res.data.lname + "\nDAC" + res.data.fname + "\nDAD" + res.data.mname +
          "\nDBD\nDBB" + DBB + "\nDBC" + DBC + "\nDAY" + res.data.eye_color +
          "\nDAU0" + DAU * 100 + " in\nDAG\nDAI" + res.data.city +
          "\nDAJ" + res.data.address + "\nDAK" + res.data.zipcode +
          "\nDAQ\nDCF\nDCG\nDDE\nDDF\nDDG";
        setTimeout(() => { this.generateBarCode(value); }, 100);
      })
      .catch(() => { this.loadingData = false; });
  },
  filters: {
    toFixedTwo(value) { return parseFloat(value).toFixed(2); },
    timeFormat(value, date) {
      if (date == null) return;
      return moment(date + " " + value).format("hh:mm a");
    },
    sDateFormate(date) {
      if (date == null) return;
      return moment(String(date)).format("dddd, MMMM DD, YYYY");
    }
  }
};
</script>
